import { useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { Icon } from '@anyroad/icon';
import { BaseModal } from '../base-modal';
import { colors, fontSizes, shape, spacing } from '@anyroad/theme';

const Content = styled.div`
  align-items: center;
  color: ${colors.text.primary};
  display: flex;
  flex-direction: column;
  font-family: Soleil;
  font-size: ${fontSizes.medium};
  gap: ${spacing.medium};
`;
Content.displayName = 'Content';

const Title = styled.div`
  font-family: Soleil;
  font-size: 18px;
  font-weight: 600;
  line-height: 24px;
`;
Title.displayName = 'Title';

const Info = styled.div`
  align-self: flex-start;
  color: ${colors.text.primary};
  font-family: Soleil;
  font-size: 14px;
  line-height: 18px;
`;
Info.displayName = 'Info';

const Name = styled.div`
  font-family: Soleil;
  font-weight: 600;
`;
Name.displayName = 'Name';

const LocationInfo = styled.div`
  align-items: center;
  color: ${colors.text.secondary};
  display: flex;
  font-family: Soleil;
  margin-top: 4px;
`;
LocationInfo.displayName = 'LocationInfo';

const IconStyles = css`
  fill: ${colors.text.icon};
  height: 16px;
  margin-right: 2px;
  width: 16px;
`;

const CopyIcon = styled(Icon)`
  height: 16px;
  width: 16px;
`;
CopyIcon.displayName = 'CopyIcon';

const CheckmarkIcon = styled(Icon)`
  height: 16px;
  width: 16px;
`;
CheckmarkIcon.displayName = 'CheckmarkIcon';

const LocationIcon = styled(Icon)`
  ${IconStyles}
`;
LocationIcon.displayName = 'LocationIcon';

const ComputerIcon = styled(Icon)`
  ${IconStyles}
  margin-right: 6px;
`;
ComputerIcon.displayName = 'ComputerIcon';

const LinkInputWrapper = styled.div`
  align-items: center;
  display: flex;
  font-family: Soleil;
  gap: ${spacing.medium};
  width: 100%;
`;
LinkInputWrapper.displayName = 'LinkInputWrapper';

const Input = styled.input`
  background: ${colors.background.light};
  border: 1px solid ${colors.borderAction};
  box-sizing: border-box;
  color: ${colors.text.secondary};
  font-family: Soleil;
  font-size: 14px;
  height: 44px;
  line-height: 18px;
  opacity: 1;
  padding: 0 ${spacing.small} 0 ${spacing.medium};
  text-overflow: ellipsis;
  width: 100%;
  -webkit-text-fill-color: ${colors.text.secondary};
`;
Input.displayName = 'Input';

const CopyLinkButton = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  color: ${colors.text.link};
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  font-family: Soleil;
  font-size: 14px;
  gap: ${spacing.xsmall};
  justify-content: flex-start;
  padding: 0;
  width: 88px;
`;
CopyLinkButton.displayName = 'CopyLinkButton';

export const DoneButton = styled.button`
  background: ${colors.primary};
  border: none;
  border-radius: ${shape.borderRadius};
  color: ${colors.white};
  cursor: pointer;
  font-family: Soleil;
  font-size: 16px;
  height: 44px;
  line-height: 18px;
  margin-top: ${spacing.xsmall};
  padding: 0 ${spacing.xlarge};
  transition: background 250ms ease;

  &:hover {
    background: #263dd6;
  }
`;
DoneButton.displayName = 'DoneButton';
export type ShareLinkModalContentProps = {
  link?: string;
  localizedStrings?: {
    copied: string;
    copyLink: string;
    done: string;
  };
  location?: string;
  onClose(): void;
  text?: string;
  title?: string;
};

const defaultLocalizedStrings = {
  copied: 'Copied',
  copyLink: 'Copy Link',
  done: 'Done',
};
export const ShareLinkModalContent = ({
  link = '',
  location,
  onClose,
  text = '',
  title = 'Share',
  localizedStrings = defaultLocalizedStrings,
}: ShareLinkModalContentProps) => {
  const [copied, setCopied] = useState(false);

  return (
    <Content>
      <Title>{title}</Title>
      <Info>
        {location ? (
          <>
            <Name>{text}</Name>
            <LocationInfo>
              {location === 'Online' ? (
                <ComputerIcon name="Computer" />
              ) : (
                <LocationIcon name="Location" />
              )}
              {location}
            </LocationInfo>
          </>
        ) : (
          text
        )}
      </Info>
      <LinkInputWrapper>
        <Input data-testid="share-link" disabled type="text" value={link} />
        <CopyToClipboard onCopy={() => setCopied(true)} text={link}>
          <CopyLinkButton>
            {copied ? (
              <>
                <CheckmarkIcon fill={colors.primary} name="Checkmark" />
                {localizedStrings.copied}
              </>
            ) : (
              <>
                <CopyIcon fill={colors.primary} name="Copy" />
                {localizedStrings.copyLink}
              </>
            )}
          </CopyLinkButton>
        </CopyToClipboard>
      </LinkInputWrapper>
      <DoneButton onClick={onClose}>{localizedStrings.done}</DoneButton>
    </Content>
  );
};
ShareLinkModalContent.displayName = 'ShareLinkModalContent';

export const ShareLinkModal = BaseModal<ShareLinkModalContentProps>(
  ShareLinkModalContent,
  {
    isCloseButtonHidden: true,
    padding: '32px',
  }
);
